import { useState } from 'react';
import { Button, Textarea } from '@indipia/ui-core';
import {
  CUST_REQ_SHARE_MESSAGE as MSG,
  CUST_REQ_SHARE_TEXT_MAX,
  countLabel,
  type CustReqShareItem,
} from './custReqCustomerShare.logic';

/**
 * 작업보고 편집·발송 오른쪽 미리보기의 '✓ 요청하신 사항' — 기획 004 F-3.
 * 요청마다 고객용 문장 1줄, [✎]로 문장 다듬기(기본 원문, 최대 1000자).
 * 발송 완료(잠금) 건은 다듬기 불가.
 */
export function CustReqSharePreview({
  entries, locked, onTextChange, onTextCommit,
}: {
  entries: readonly { item: CustReqShareItem; text: string; raw: string }[];
  locked: boolean;
  /** 입력 중 값(저장 안 함) */
  onTextChange: (item: CustReqShareItem, text: string) => void;
  /** 입력칸을 벗어날 때 저장(완료·✎ 버튼을 누르면 입력칸이 먼저 포커스를 잃어 저장됨) */
  onTextCommit: (item: CustReqShareItem) => void;
}) {
  const [editingId, setEditingId] = useState<number | null>(null);
  if (entries.length === 0) return null;

  return (
    <div className="mt-2.5">
      <div className="mb-1 text-caption font-semibold text-[var(--indipia-purple-500)]">{MSG.previewTitle}</div>
      <ul className="space-y-1.5">
        {entries.map(({ item, text, raw }) => {
          const editing = editingId === item.custReqId && !locked;
          return (
            <li key={item.custReqId} className="list-none rounded-md border border-border bg-surface px-3 py-2">
              <div className="flex items-start gap-2">
                <p className="min-w-0 flex-1 whitespace-pre-line break-words text-body-sm leading-relaxed text-text">
                  {text}
                </p>
                {!locked && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={MSG.editText}
                    aria-expanded={editing}
                    onClick={() => setEditingId(editing ? null : item.custReqId)}
                  >
                    ✎
                  </Button>
                )}
              </div>

              {editing && (
                <div className="mt-2">
                  <Textarea
                    className="field-auto"
                    label={MSG.textLabel}
                    value={raw}
                    maxLength={CUST_REQ_SHARE_TEXT_MAX}
                    onChange={(e) => onTextChange(item, e.target.value)}
                    onBlur={() => onTextCommit(item)}
                  />
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-caption text-text-muted">{MSG.textResetHint}</span>
                    <span className="text-caption text-text-muted">{countLabel(raw)}</span>
                  </div>
                  <div className="mt-1 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditingId(null)}
                    >
                      {MSG.editDone}
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
